import { getFeatureColor, getSignOutColor } from './MapColorManager';

/**
 * Centralized Mapbox style definitions
 * These must be JavaScript objects for MapboxGL, but colors come from our color system
 */

// Drawing tool styles
export const DRAW_STYLES = [
  // Polygon fill - NO FILL by default
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: {
      'fill-color': 'transparent',
      'fill-outline-color': 'transparent',
      'fill-opacity': 0
    }
  },
  // Polygon outline - THICK ORANGE
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-color': getFeatureColor('polygon', 'default'),
      'line-width': 4, // Thick line
      'line-dasharray': [0.2, 2]
    }
  },
  // Polygon outline - THICK ORANGE (inactive)
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-color': getFeatureColor('polygon', 'default'),
      'line-width': 3, // Slightly thinner when not active
      'line-dasharray': [0.2, 2]
    }
  },
  // Line - ACTIVE
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-color': '#ffff00', // Yellow color
      'line-dasharray': [0.2, 2],
      'line-width': 2
    }
  },
  // Line - INACTIVE
  {
    id: 'gl-draw-line-inactive',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'active', 'true']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round'
    },
    paint: {
      'line-color': '#ffff00', // Yellow color
      'line-dasharray': [0.2, 2],
      'line-width': 2
    }
  },
  // Point - ACTIVE
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'active', 'true']],
    paint: {
      'circle-radius': 6,
      'circle-color': getFeatureColor('point', 'default')
    }
  },
  // Point - INACTIVE
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['!=', 'active', 'true']],
    paint: {
      'circle-radius': 5,
      'circle-color': getFeatureColor('point', 'default')
    }
  }
];

// Feature layer styles
export const FEATURE_STYLES = {
  stand: {
    type: 'circle',
    paint: {
      'circle-radius': 8,
      'circle-color': getFeatureColor('stand', 'hunting'),
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  },
  food_plot: {
    type: 'fill',
    paint: {
      'fill-color': getFeatureColor('food_plot', 'hunting'),
      'fill-opacity': 0.6,
      'fill-outline-color': getFeatureColor('food_plot', 'hunting')
    }
  },
  boundary: {
    type: 'line',
    paint: {
      'line-color': getFeatureColor('boundary', 'default'),
      'line-width': 4, // Thick line
      'line-dasharray': [2, 2]
    }
  },
  polygon: {
    type: 'line',
    paint: {
      'line-color': getFeatureColor('polygon', 'default'),
      'line-width': 4, // Thick line
      'line-dasharray': [0.2, 2]
    }
  },
  // Line features (trails, paths, etc.)
  line: {
    type: 'line',
    paint: {
      'line-color': getFeatureColor('line', 'default'),
      'line-width': 2,
      'line-dasharray': [0.2, 2]
    }
  },
  trail: {
    type: 'line',
    paint: {
      'line-color': getFeatureColor('trail', 'default'),
      'line-width': 2,
      'line-dasharray': [0.2, 2]
    }
  },
  // Point features (waypoints, markers, etc.)
  point: {
    type: 'circle',
    paint: {
      'circle-radius': 6,
      'circle-color': getFeatureColor('point', 'default'),
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1
    }
  },
  waypoint: {
    type: 'circle',
    paint: {
      'circle-radius': 7,
      'circle-color': getFeatureColor('waypoint', 'default'),
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  },
  water_source: {
    type: 'circle',
    paint: {
      'circle-radius': 8,
      'circle-color': getFeatureColor('water_source', 'default'),
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  },
  // Hunt location with sign-out status colors
  hunt_location: {
    type: 'circle',
    paint: {
      'circle-radius': 8,
      'circle-color': [
        'case',
        ['==', ['get', 'status'], 'occupied'], getSignOutColor('occupied'),
        ['==', ['get', 'status'], 'pending'], getSignOutColor('pending'),
        getSignOutColor('available') // Default to available (green)
      ],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  }
};

// Function to create polygon layer with no fill and thick orange outline
export function createPolygonLayer(layerId, sourceId) {
  return {
    id: layerId,
    type: 'line', // Use line type instead of fill for no fill
    source: sourceId,
    filter: ['==', '$type', 'Polygon'],
    paint: {
      'line-color': getFeatureColor('polygon', 'default'),
      'line-width': 4, // Thick
      'line-dasharray': [0.2, 2]
    }
  };
}

// Function to create line layer
export function createLineLayer(layerId, sourceId) {
  return {
    id: layerId,
    type: 'line',
    source: sourceId,
    filter: ['==', '$type', 'LineString'],
    paint: {
      'line-color': getFeatureColor('line', 'default'),
      'line-width': 2,
      'line-dasharray': [0.2, 2]
    }
  };
}

// Function to create point layer
export function createPointLayer(layerId, sourceId) {
  return {
    id: layerId,
    type: 'circle',
    source: sourceId,
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-radius': 6,
      'circle-color': getFeatureColor('point', 'default'),
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1
    }
  };
}

// Function to create hunt location layer with sign-out status colors
export function createHuntLocationLayer(layerId, sourceId) {
  return {
    id: layerId,
    type: 'circle',
    source: sourceId,
    filter: ['==', '$type', 'Point'],
    paint: {
      'circle-radius': 8,
      'circle-color': [
        'case',
        ['==', ['get', 'status'], 'occupied'], getSignOutColor('occupied'),
        ['==', ['get', 'status'], 'pending'], getSignOutColor('pending'),
        getSignOutColor('available') // Default to available (green)
      ],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  };
}

// Function to get styles for a specific feature type
export function getFeatureStyle(featureType, colorScheme = 'default') {
  const baseStyle = FEATURE_STYLES[featureType];
  if (!baseStyle) return null;
  
  return {
    ...baseStyle,
    paint: {
      ...baseStyle.paint,
      ...(baseStyle.paint['circle-color'] && {
        'circle-color': getFeatureColor(featureType, colorScheme)
      }),
      ...(baseStyle.paint['fill-color'] && {
        'fill-color': getFeatureColor(featureType, colorScheme),
        'fill-outline-color': getFeatureColor(featureType, colorScheme)
      }),
      ...(baseStyle.paint['line-color'] && {
        'line-color': getFeatureColor(featureType, colorScheme)
      })
    }
  };
}

// Function to create drawing configuration with our styles
export function createDrawConfig(additionalConfig = {}) {
  const { controls: extraControls, ...rest } = additionalConfig;
  return {
    displayControlsDefault: false,
    controls: {
      polygon: true,
      // Keep both to satisfy tests that check 'line' while Draw expects 'line_string'
      line: true,
      line_string: true,
      point: false,
      trash: false,
      ...(extraControls || {}),
    },
    defaultMode: 'simple_select',
    styles: DRAW_STYLES,
    ...rest,
  };
}
